import type { CtxType } from '../client-export'
import {
  handleOnClientAppGlobal,
  handleOnClientFirstPage,
  handleOnClientRouteChangePage,
} from './lib/browser'
import { handleOnServerExport, handleOnServerSSR } from './lib/server'
import './lib/browser_global'

export type PrefetchApiConfigType = {
  [propKey: string]: PrefetchApiConfigItemType
}
export type PrefetchApiConfigItemType = {
  critical?: boolean // 是否为关键路径，默认为 false
  enabled?: string | boolean // 是否当前可执行任务，默认为 true。使用自定义表达式的逻辑布尔值描述
  method?: 'GET' | 'POST' // 请求方法，默认为 GET。支持 GET、POST 两种方法
  url: string // 请求地址，通常为相对地址，由 gateway 配置补全
  data?: object // 传输数据，对象内部的 value 使用自定义表达式描述
  type?: 'URL' | 'JSON' // 使用的传输格式，默认为 URL（www-form-urlencoded）
  headers?: object // 自定义请求头，对象内部的 value 使用自定义表达式描述
}

export type PrefetchApiFallbackType = (
  propKey: string,
  expressionParam: {
    $location: {
      protocol: 'http:' | 'https:'
      host: string
      pathname: string
    }
    $cookie: { [key: string]: any }
    $query: { [key: string]: any }
    $platform: any // 待废弃
  }
) => Promise<object | undefined>

export type PrefetchApiCallbackType = (
  prefetchResult: PrefetchApiPropsType,
  propKey: string,
  ctx: CtxType
) => undefined | { [newPropKey: string]: undefined | object | Promise<object> }

export type PrefetchApiPropsType = {
  [propKey: string]: PrefetchApiPropItemType
}
export type PrefetchApiPropItemType = {
  status: 'INITIAL' | 'CANCEL' | 'LOADING' | 'COMPLETE'
  value?: object
  isReady: boolean
}

export interface PrefetchApiMock {
  [propKey: string]: ResponseType
}

export type TaskType = {
  config: {
    critical: boolean
    enabled: string | boolean
    method: 'GET' | 'POST'
    url: string
    data: object
    type: 'url' | 'json'
    headers?: object
  }
  propKey: string
  category: 'global' | 'page'
  status: 'INITIAL' | 'CANCEL' | 'FALLBACK' | 'LOADING' | 'COMPLETE'
  request: undefined | { then: (cb: () => void) => Promise<void> }
  response: undefined | object
  fallback: undefined | { then: (cb: () => void) => Promise<void> }
}

export type InnerFuncParamType = {
  ctx: CtxType
  onPropChange?: (propKey: string) => void
  onComplete?: () => void
  convertRawResponse: (task: TaskType) => any
  sitePrefetchApiConfig?: any
  globalPrefetchApi?: PrefetchApiConfigType
  pagePrefetchApi?: PrefetchApiConfigType
  pageFallback?: PrefetchApiFallbackType
  pageCallback?: PrefetchApiCallbackType
  globalMock?: PrefetchApiMock
  pageMock?: PrefetchApiMock
}

// 转换为内部使用的参数对象，方便在多个方法间传递参数
export function toInnerFuncParam(
  ctx: CtxType,
  pageConfig?: any,
  onPropChange?: (propKey: string) => void,
  onComplete?: () => void
): InnerFuncParamType {
  const internalScope = ctx.__AL_EX_internalScope
  return {
    ctx,
    onPropChange,
    onComplete,
    convertRawResponse: (task) => {
      const extraStoreValue = (task as any)
        .__AL_EX_FW_prefetchApi_extraStoreValue
      if (extraStoreValue) {
        return extraStoreValue
      }
      if (internalScope.globalConfig.convertRawResponseToSiteResponse) {
        return internalScope.globalConfig.convertRawResponseToSiteResponse(
          task.propKey,
          task.response,
          ctx
        )
      }
      return task.response
    },
    sitePrefetchApiConfig:
      internalScope.runtimeConfig.frameworkConfig.prefetchApi,
    globalPrefetchApi: internalScope.globalConfig.prefetchApi,
    pagePrefetchApi: pageConfig?.prefetchApi,
    pageFallback: pageConfig?.prefetchApiFallback,
    pageCallback: pageConfig?.prefetchApiCallback,
    globalMock: internalScope.globalConfig.prefetchApiMock,
    pageMock: pageConfig?.prefetchApiMock,
  }
}

// server ssr/client 首页 时，解析并等待完成任务(global+page)，最终 prop 状态为 CANCEL 或 COMPLETE
// server export/client 首页时，解析任务(global+page)并返回状态为 INITIAL 的空 prop 先行渲染，在 client 再次启动
// client routeChange 时，已经历 startOnAppConstructor，global 任务均已就绪，不处理
export async function startOnAppGetInitialProps(
  param: InnerFuncParamType
): Promise<{
  renderProps: PrefetchApiPropsType
  clientTaskConf?: object
  clientTaskMockData?: object
}> {
  let result: any = {
    renderProps: {},
    clientTaskConf: undefined,
    clientTaskMockData: undefined,
    globalPropKeys: Object.keys(param.globalPrefetchApi || {}),
    pagePropsKeys: Object.keys(param.pagePrefetchApi || {}),
  }
  if (process.server) {
    if (param.ctx.lifecycleStatus.isSsr) {
      result.renderProps = await handleOnServerSSR(param)
      return result
    } else if (param.ctx.lifecycleStatus.isExport) {
      const {
        resultProps,
        pageTasksConf,
        pageTasksMockData,
      } = await handleOnServerExport(param)
      result.renderProps = resultProps
      result.clientTaskConf = pageTasksConf
      result.clientTaskMockData = pageTasksMockData
      return Promise.resolve(result)
    } else {
      throw new Error('BUG')
    }
  } else {
    return Promise.resolve(result)
  }
}

// server ssr/client 首页 时，global、page 的 props 都已传入，状态为 CANCEL 或 COMPLETE
// server export/client 首页时，global、page 的 props 都已传入，状态为 INITIAL，且 head 脚本已开始启动任务
// client routeChange 时，global 一定已处理，不在处理
// withApp 仅处理 global，page 由 withPage 处理
export function startOnAppConstructor(
  param: InnerFuncParamType
): PrefetchApiPropsType {
  // 考虑 App 作为一个公共父容器，总是应该稳定的提供 global props
  // 总是在 client firstPage 时，完成 global 初始化
  if (process.server) {
    return {}
  }
  if (!param.ctx.lifecycleStatus.isFirstPage) {
    throw new Error('框架时序异常')
  }
  // export 需要处理已启动任务，export 下的 routeChange 需要确保 global props 状态
  return handleOnClientAppGlobal(param)
}

// 只讨论 page，global 的已在 withApp 处理
// server ssr/client 首页 时，传入的 props 已达到 CANCEL 或 COMPLETE 终态，不需要在再处理
// server export/client 首页时，传入的 props 状态为 INITIAL，且 head 脚本已开始启动任务
// client routeChange 时，需要自行启动 global 任务
export function startOnPageConstructor(param: InnerFuncParamType) {
  if (process.server) {
    // server
    return {}
  }
  if (param.ctx.lifecycleStatus.isFirstPage) {
    // export 需从已启动的任务中生成新 props
    // ssr 保持逻辑一致
    return handleOnClientFirstPage(param)
  } else {
    // routeChange，解析任务并启动
    return handleOnClientRouteChangePage(param)
  }
}
